import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiEye, FiSearch, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { formatSingaporeDateTime, formatSingaporeDateTimeDmyAmPm } from '../utils/singaporeTime';

const formatArmyLabel = (army) => {
    if (!army) return '-';
    const name = army.name || '-';
    const username = army.tgusername
        ? `@${String(army.tgusername).replace(/^@/, '')}`
        : '';
    return username ? `${name} - ${username}` : name;
};

const formatReviewFor = (row) => {
    if (row?.SupermanMember) {
        const m = row.SupermanMember;
        return `Superman: ${m.name || '—'} (${m.webID || 'no webID'})`;
    }
    if (row?.godMember) {
        const m = row.godMember;
        return `God: ${[m.godID, m.godName, m.webID].filter(Boolean).join(' - ') || '—'}`;
    }
    return '—';
};

const formatDeliveryPlatforms = (platforms) => {
    if (!Array.isArray(platforms) || !platforms.length) return '—';
    if (typeof platforms[0] === 'string') {
        return platforms.map((name) => String(name || '').trim()).filter(Boolean).join('\n') || '—';
    }
    return platforms.map((row) => {
        const name = String(row?.platform || row?.name || '').trim() || '—';
        if (!row?.dateTime) return name;
        return `${name} (${formatSingaporeDateTimeDmyAmPm(row.dateTime)})`;
    }).join('\n');
};

const SupermanReviewPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('done');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [selected, setSelected] = useState(null);
    const [showView, setShowView] = useState(false);
    const [viewLoading, setViewLoading] = useState(false);

    const authHeaders = useCallback(() => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    }), []);

    const fetchItems = useCallback(async (page = 1, search = '', status = 'done') => {
        try {
            setLoading(true);
            setError('');
            let url = `${process.env.REACT_APP_API_URL}/api/supermandeliveries?page=${page}&limit=20&sort=-reviewDateandTime`;
            if (status) url += `&status=${encodeURIComponent(status)}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            const res = await axios.get(url, { headers: authHeaders() });
            if (res.data.success) {
                setItems(res.data.data || []);
                setTotal(res.data.pagination?.total || 0);
                setTotalPages(res.data.pagination?.pages || 1);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [authHeaders]);

    useEffect(() => {
        fetchItems(currentPage, searchTerm, statusFilter);
    }, [currentPage, searchTerm, statusFilter, fetchItems]);

    const openView = async (row) => {
        setShowView(true);
        setViewLoading(true);
        setSelected(row);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/supermandeliveries/${row._id}`,
                { headers: authHeaders() }
            );
            if (res.data.success) setSelected(res.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setViewLoading(false);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold">Superman Reviews</h1>
                    <p className="text-gray-600">Total: {total}</p>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                    <div className="relative flex-1 min-w-[220px] max-w-xl">
                        <FiSearch className="absolute left-3 top-3 text-gray-400" />
                        <input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearchTerm(searchInput);
                                    setCurrentPage(1);
                                }
                            }}
                            placeholder="Search comment, type, sender, member..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                        <option value="done">Done</option>
                        <option value="">All statuses</option>
                        <option value="pending">Pending</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="skipped">Skipped</option>
                    </select>
                    <button
                        type="button"
                        onClick={() => { setSearchTerm(searchInput); setCurrentPage(1); }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Search
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setSearchInput('');
                            setSearchTerm('');
                            setStatusFilter('done');
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                    >
                        Clear
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review Type</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platforms</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review Comment</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sender</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Review For</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={7} className="px-4 py-6 text-center">Loading...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No reviews found</td></tr>
                            ) : (
                                items.map((row) => (
                                    <tr key={row._id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                                            {formatSingaporeDateTime(row.reviewDateandTime || row.reviewDate)}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{row.type || 'Massage Review'}</td>
                                        <td className="px-4 py-3 text-sm max-w-[260px] whitespace-pre-line text-xs leading-5" title={formatDeliveryPlatforms(row.platforms)}>
                                            {formatDeliveryPlatforms(row.platforms)}
                                        </td>
                                        <td className="px-4 py-3 text-sm max-w-[280px] truncate" title={row.reviewComment || ''}>
                                            {row.reviewComment || '—'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{formatArmyLabel(row.sender)}</td>
                                        <td className="px-4 py-3 text-sm text-blue-700">{formatReviewFor(row)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                type="button"
                                                onClick={() => openView(row)}
                                                className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-full"
                                                title="View"
                                            >
                                                <FiEye />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

                {showView && selected && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-bold">Review Details</h2>
                                <button
                                    type="button"
                                    onClick={() => { setShowView(false); setSelected(null); }}
                                    className="text-gray-500"
                                >
                                    <FiX size={20} />
                                </button>
                            </div>
                            {viewLoading ? (
                                <p className="text-gray-500">Loading...</p>
                            ) : (
                                <div className="space-y-3 text-sm">
                                    <p>
                                        <span className="text-gray-600">Review Date:</span>{' '}
                                        <span className="font-medium">
                                            {formatSingaporeDateTime(selected.reviewDateandTime || selected.reviewDate)}
                                        </span>
                                    </p>
                                    <p>
                                        <span className="text-gray-600">Review Type:</span>{' '}
                                        <span className="font-medium">{selected.type || 'Massage Review'}</span>
                                    </p>
                                    <p>
                                        <span className="text-gray-600">Sender:</span>{' '}
                                        <span className="font-medium">{formatArmyLabel(selected.sender)}</span>
                                    </p>
                                    <p>
                                        <span className="text-gray-600">Review For:</span>{' '}
                                        <span className="font-medium text-blue-700">{formatReviewFor(selected)}</span>
                                    </p>
                                    <p>
                                        <span className="text-gray-600">Status:</span>{' '}
                                        <span className="font-medium capitalize">{selected.status || '—'}</span>
                                    </p>
                                    <p>
                                        <span className="text-gray-600">Platforms:</span>{' '}
                                        <span className="font-medium whitespace-pre-line leading-5">
                                            {formatDeliveryPlatforms(selected.platforms)}
                                        </span>
                                    </p>
                                    <div>
                                        <p className="text-gray-600 mb-1">Review Comment:</p>
                                        <p className="whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-3">
                                            {selected.reviewComment || '—'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-600 mb-1">Remarks:</p>
                                        <p className="whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-md p-3">
                                            {selected.remarks || '—'}
                                        </p>
                                    </div>
                                    {selected.payment && (
                                        <p>
                                            <span className="text-gray-600">Package:</span>{' '}
                                            {Number(selected.payment.paymentAmount || 0).toFixed(2)} SGD
                                            {' · '}
                                            bal {selected.payment.reviewBal ?? 0}/{selected.payment.reviewCount ?? 0}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SupermanReviewPage;
